import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Box, Text, Modal } from '@rsos/components/capstone/base';
import {
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@rsos/components/capstone/base/Modal/ModalComponents';
import { AWAITING_CUSTOMER_REVIEW } from '../../../../constants/issueStatuses';
import GoogleMap from '../GoogleMap';
import CommentArea from './CommentArea/';
import MapControlsJBoundary from './MapControlsJBoundary';
import { Container, ReviewModalButton } from './ReviewModal.styles';
import {
  SUBMITTED_LAYER_STYLE,
  PROCESSED_LAYER_STYLE,
  BOUNDING_BOX_LAYER_STYLE,
} from './constants';
import { initialState, calculateBoundsCenter } from './utils';

const ReviewModal = ({ isOpened, onClose, issue, onComment, onVerify }) => {
  const [isMapReady, setIsMapReady] = useState(false);
  const [isEditing, setIsEditing] = useState(initialState.isEditing);
  const [mapControls, setMapControls] = useState(initialState.mapControls);
  const [comment, setComment] = useState(initialState.comment);
  const [mapLayers, setMapLayers] = useState(initialState.mapLayers);
  const [map, setMap] = useState(initialState.map);

  const { updating, commenting } = useSelector(state => state.issues);

  const initPolygonLayers = (submitted, processed, boundingBox) => {
    const submittedLayer = new window.google.maps.Data({ map });
    const processedLayer = new window.google.maps.Data({ map });
    const boundingBoxLayer = new window.google.maps.Data({ map });

    submittedLayer.addGeoJson(submitted);
    submittedLayer.setStyle(SUBMITTED_LAYER_STYLE);

    processedLayer.addGeoJson(processed);
    processedLayer.setStyle(PROCESSED_LAYER_STYLE);

    boundingBoxLayer.addGeoJson(boundingBox);
    boundingBoxLayer.setStyle(BOUNDING_BOX_LAYER_STYLE);

    setMapLayers({
      submittedPolygon: submittedLayer,
      processedPolygon: processedLayer,
      boundingBox: boundingBoxLayer,
    });
  };

  useEffect(() => {
    if (isMapReady) {
      const { original_shape, processed_shape, bounding_box } = issue;
      if (map) {
        if (
          !Object.keys(original_shape).length ||
          !Object.keys(processed_shape).length
        ) {
          return;
        }
        initPolygonLayers(original_shape, processed_shape, bounding_box);
      }
    }
    // This effect should only run once the isMapReady is set to true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapReady]);

  const centerMap = useCallback(() => {
    const bounds = new window.google.maps.LatLngBounds();
    mapLayers.processedPolygon.forEach(function (feature) {
      feature.getGeometry().forEachLatLng(function (latlng) {
        bounds.extend(latlng);
      });
    });
    map.fitBounds(bounds);
  }, [map, mapLayers]);

  useEffect(() => {
    if (mapLayers.processedPolygon) {
      centerMap();
    }
  }, [mapLayers, centerMap]);

  const toggleCommentArea = () => {
    setIsEditing(prev => !prev);
  };
  const handleEditingChange = event => {
    const { value } = event.target;
    setComment(value);
  };
  const resetIsEditing = () => {
    setComment('');
    setIsEditing(false);
  };
  const handleComment = () => {
    onComment(issue, comment, () => resetIsEditing());
  };

  const handleVerify = () => {
    onVerify(issue);
  };

  const handleMapLoaded = map => {
    setMap(map);
    setIsMapReady(true);
  };

  const showPolygon = layer => {
    if (!layer) {
      // This should not happen if polygons are returned in a correct format
      // by the API, but just to be safe...
      return;
    }
    layer.setMap(map);
  };

  const hidePolygon = layer => {
    if (!layer) {
      // This should not happen if polygons are returned in a correct format
      // by the API, but just to be safe...
      return;
    }
    layer.setMap(null);
  };

  const handleMapControlChange = e => {
    const { name, checked } = e.target;
    setMapControls({
      ...mapControls,
      [name]: checked,
    });

    const layer = mapLayers[name];
    if (checked) {
      showPolygon(layer);
    } else {
      hidePolygon(layer);
    }
  };
  const editingDisableState =
    updating || issue.status !== AWAITING_CUSTOMER_REVIEW;

  return (
    <Modal
      isOpened={isOpened}
      width={560}
      padding="40px 14px"
      id="modal-review-jurisdiction-details"
      maxHeight="100%"
    >
      <ModalHeader onClose={onClose}>
        <Box>
          <Text size="header" fontWeight="semibold" color="secondaryText">
            {isEditing ? 'Seek Clarification' : 'Review your JV Boundary'}
          </Text>
          <Text size="small" my="15px" color="secondaryText">
            {isEditing ? (
              <>
                Please leave any comments or questions for the RapidSOS team
                below.
              </>
            ) : (
              <>
                Please review your geofence for query view and shapefile for
                jurisdiction view. The geofence for query view has been updated
                to include a buffer area based on your agency's shapefile.
              </>
            )}
          </Text>
        </Box>
      </ModalHeader>
      <ModalBody>
        <Container>
          <MapControlsJBoundary
            mapControls={mapControls}
            handleMapControlChange={handleMapControlChange}
          />

          <GoogleMap
            id="admin-config-review-coverage-map"
            style={{
              width: '100%',
              height: 400,
            }}
            onMapReady={handleMapLoaded}
            options={{
              zoom: 3,
              center: calculateBoundsCenter({
                north: 48.999968,
                south: 23.634763,
                east: -65.524514,
                west: -125.979106,
              }),
              clickableIcons: false,
              mapTypeControl: true,
              zoomControl: true,
              zoomControlOptions: {
                position: 3, // google.maps.ControlPosition.TOP_RIGHT
              },
              mapTypeControlOptions: {
                style: 1, // google.maps.MapTypeControlStyle.HORIZONTAL_BAR
                position: 3, // google.maps.ControlPosition.TOP_RIGHT
              },
              scaleControl: true,
              streetViewControl: true,
              streetViewControlOptions: {
                position: 6, // google.maps.ControlPosition.LEFT_BOTTOM
              },
              fullscreenControl: true,
              fullscreenControlOptions: {
                position: 12, // google.maps.ControlPosition.BOTTOM_RIGHT
              },
            }}
          />
        </Container>
        <CommentArea
          comments={issue.comments}
          isEditing={isEditing}
          onChange={handleEditingChange}
          comment={comment}
        />
      </ModalBody>
      <ModalFooter flow={12} justify="center">
        <ReviewModalButton
          btnType="primary"
          disabled={
            isEditing ? commenting || updating || !comment : editingDisableState
          }
          onClick={isEditing ? handleComment : handleVerify}
          isLoadingProp={commenting || updating}
        >
          {isEditing ? 'Send' : 'Verify'}
        </ReviewModalButton>
        <ReviewModalButton
          btnType="tertiary"
          onClick={toggleCommentArea}
          disabled={!isEditing && editingDisableState}
        >
          {isEditing ? 'Go Back' : 'Seek Clarification'}
        </ReviewModalButton>
      </ModalFooter>
    </Modal>
  );
};

export default ReviewModal;
